<template>
  <v-container fluid>
    <back-link-component :to="{ name: 'FieldListAsList' }" :label="$tt('customFieldsList')" />
    <v-row align-center justify-space-between>
      <v-col cols="12" md="5">
        <v-sheet color="aid-bg-grey" class="px-4 pb-8 pt-4">
          <v-layout align-center justify-start>
            <v-sheet class="transparent">
              <h1 v-if="field">
                {{ field.name }}
              </h1>
            </v-sheet>
            <v-icon v-if="isMyField" left class="pl-2" @click="openFieldMetaDataEditor()">
              mdi-pencil
            </v-icon>
          </v-layout>
          <v-sheet class="transparent px-2 pt-4">
            <div class="font-weight-bold mb-0" v-if="field">
              <user-icon-component
                class="pr-2"
                size="60"
                :showNickname="true"
                :iconUrl="getAccount(field).iconUrl"
                :nickname="getAccount(field).nickname"
              />
              {{ getAccount(field).nickname }}
            </div>
          </v-sheet>
          <v-row no-gutters class="px-4 pt-8">
            <v-col cols="12">
              <v-divider />
              <!---------------------->
              <!--      面積        -->
              <!---------------------->
              <v-row>
                <v-col sm="4" cols="2" class="py-2">
                  <v-layout align-center justify-start>
                    <v-sheet width="32" class="transparent">
                      <v-img class="align-center" src="/icons/WorkHistory_icon_area.png" />
                    </v-sheet>
                    <v-sheet class="ml-2 font-weight-bold  subtitle-2 transparent">
                      {{ $tt('customArea') }}
                    </v-sheet>
                  </v-layout>
                </v-col>
                <v-col v-if="field">
                  <v-sheet
                    class="font-weight-bold subtitle-2 transparent"
                    v-html="this.$options.filters.aidAreaText(field.area, true)"
                  />
                </v-col>
              </v-row>
              <v-divider />
              <!---------------------->
              <!--      作付        -->
              <!---------------------->
              <v-row>
                <v-col sm="4" cols="2" class="py-2">
                  <v-layout align-center justify-start>
                    <v-sheet width="32" class="transparent">
                      <v-icon class="ml-1">eco</v-icon>
                    </v-sheet>
                    <v-sheet class="ml-2 font-weight-bold subtitle-2 transparent">
                      {{ $tt('customProduct') }}
                    </v-sheet>
                  </v-layout>
                </v-col>
                <v-col>
                  <!-- 今現在の作付 -->
                  <v-sheet class="subtitle-2 transparent" style="overflow-wrap:  break-word;" v-if="currentPlantings">
                    <div v-for="p in currentPlantings">
                      <planting-indicator-component :planting="p" />
                    </div>
                  </v-sheet>
                  <!-- 過去の作付 -->
                  <v-sheet v-if="pastPlantings.length > 0" class="subtitle-2 transparent text-left">
                    <v-btn text class="mx-0 px-0" @click="togglePastPlantings()">
                      {{ togglePastPlantingsButtonCaption }}
                      <v-icon>mdi-chevron-double-right</v-icon>
                    </v-btn>
                    <div v-for="planting in pastPlantings" :key="planting.id" v-if="showingPastPlantings">
                      <planting-indicator-component :planting="planting" />
                    </div>
                  </v-sheet>
                </v-col>
              </v-row>
              <v-divider />
              <!---------------------->
              <!--      備考        -->
              <!---------------------->
              <v-row>
                <v-col sm="4" cols="2" class="py-2">
                  <v-layout align-center justify-start>
                    <v-sheet width="32" class="transparent">
                      <v-img class="align-center" src="/icons/WorkHistory_icon_note.png" />
                    </v-sheet>
                    <v-sheet class="ml-2 font-weight-bold subtitle-2 transparent ">
                      {{ $tt('DocFormFieldDetailDescriptionHint') }}
                      <v-icon v-if="isMyField" left class="pl-2" @click="openFieldMetaDataEditor()">
                        mdi-pencil
                      </v-icon>
                    </v-sheet>
                  </v-layout>
                </v-col>
                <v-col cols="7" class="py-2">
                  <v-sheet
                    v-if="field"
                    class="subtitle-2 transparent font-weight-regular"
                    style="overflow-wrap: break-word;"
                  >
                    {{ field.caption }}
                  </v-sheet>
                </v-col>
              </v-row>
              <v-divider />
              <!---------------------------->
              <!-- 圃場属性 (有効期間・作目) -->
              <!---------------------------->
              <v-row>
                <v-col sm="4" cols="2" class="py-2">
                  <v-layout align-center justify-start>
                    <v-sheet width="32" class="transparent">
                      <v-icon class="ml-1">mdi-tag-outline</v-icon>
                    </v-sheet>
                    <v-sheet class="ml-2 font-weight-bold subtitle-2 transparent">
                      {{ $tt('feed.fieldAttrsTitle') }}
                      <v-icon v-if="isMyField" left class="pl-2" @click="openFieldAttrsEditor()">
                        mdi-pencil
                      </v-icon>
                    </v-sheet>
                  </v-layout>
                </v-col>
                <v-col class="py-2">
                  <v-sheet class="subtitle-2 transparent font-weight-regular">
                    <div v-if="fieldAttrs.valid_from">
                      {{ $tt('feed.fieldValidFromDate', { date: fieldAttrs.valid_from }) }}
                    </div>
                    <div v-if="fieldAttrs.valid_to">
                      {{ $tt('feed.fieldValidUntil', { date: fieldAttrs.valid_to }) }}
                    </div>
                    <div v-if="fieldAttrs.crop">{{ $tt('feed.mapSetCrop') }}: {{ fieldAttrs.crop }}</div>
                  </v-sheet>
                </v-col>
              </v-row>
              <v-divider />
              <!---------------------->
              <!--   高低差マップ     -->
              <!---------------------->
              <v-row>
                <v-col sm="4" cols="2" class="py-2">
                  <v-layout align-center justify-start>
                    <v-sheet width="32" class="transparent">
                      <v-img class="align-center" src="/icons/Field_Elevation.png" />
                    </v-sheet>
                    <v-sheet class="ml-2 font-weight-bold subtitle-2 transparent">
                      {{ $tt('fieldElevation.title') }}
                    </v-sheet>
                  </v-layout>
                </v-col>
                <v-col v-if="field">
                  <v-sheet class="subtitle-2 transparent">
                    <div v-if="!isMyField">
                      <field-elevation-summary-component :field-elevation-map="currentFieldElevationMap" />
                    </div>
                    <div v-if="isMyField">
                      <div>
                        <div v-if="countTasksInCurrentField <= 0">
                          {{ $tt('fieldElevation.cantBuildBecauseNoTask') }}
                        </div>
                        <div v-if="countTasksInCurrentField > 0">
                          <div v-if="currentFieldElevationMap">
                            <div v-if="currentFieldElevationMap.status === 'Failed'">
                              <v-btn small class="primary" @click="openElevationMapEditor()">
                                {{ $tt('fieldElevation.createButton') }}
                              </v-btn>
                            </div>
                            <div v-if="currentFieldElevationMap.status === 'Creating'">
                              <v-progress-circular indeterminate size="18" />
                              {{ $tt('fieldElevation.nowBuilding') }}
                            </div>
                            <div v-if="currentFieldElevationMap.status === 'Completed'">
                              <field-elevation-summary-component :field-elevation-map="currentFieldElevationMap" />
                              <div>
                                <v-btn small class="primary" @click="openElevationMapEditor()">
                                  {{ $tt('fieldElevation.editButton') }}
                                </v-btn>
                              </div>
                            </div>
                          </div>
                          <div v-else>
                            <v-btn small class="primary" @click="openElevationMapEditor()">
                              {{ $tt('fieldElevation.createButton') }}
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-sheet>
                </v-col>
              </v-row>
              <v-divider />
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>

      <v-col cols="12" md="7" style="position:relative;" width="auto">
        <gmap-map
          ref="googleMapRef"
          class="mt-4"
          :center="currentMapCenterForGoogle"
          :zoom="zoomLevel"
          :options="{
            streetViewControl: false,
            rotateControl: false,
            gestureHandling: 'greedy',
            mapTypeControlOptions: {
              position: 1,
            },
            mapId: vectorMapId,
          }"
          map-type-id="roadmap"
          style="height: 70vh"
        >
          <!---------------------->
          <!-- 高低差マップゲージ -->
          <!---------------------->
          <div class="elevationColorGaugeContainer aid-bg-grey" v-if="showElevationColorGauge">
            <v-switch v-model="showElevationMap" dense :label="$tt('fieldElevation.title')" class="ml-2" />
            <e-charts
              :options="elevationColorRangeOptions"
              autoresize
              :theme="echartsTheme"
              class="elevationColorGauge aid-bg-grey"
              v-if="showElevationMap"
            />
          </div>
          <!---------------------->
          <!-- 地図マーカーピン -->
          <!---------------------->
          <gmap-marker
            :key="index"
            v-for="(marker, index) in markers"
            :position="marker.position"
            :clickable="true"
            :label="marker.label"
          />
          <!---------------------->
          <!-- 圃場編集ボタン -->
          <!---------------------->
          <div v-if="isMyField" class="polygon-edit-btn on-map-btn" @click="showEditPolygon()">
            <v-icon>mdi-pencil</v-icon>
          </div>
        </gmap-map>
      </v-col>
    </v-row>

    <!-- ------------ -->
    <!-- polygon編集 -->
    <!-- ------------ -->
    <v-dialog v-model="isShowEditPolygon" max-width="80vw" persistent eager>
      <v-card class="pt-0">
        <gmap-map
          ref="editPolygonMap"
          class="mt-4"
          :center="currentMapCenterForGoogle"
          :zoom="zoomLevel"
          :options="{ streetViewControl: false, rotateControl: false }"
          map-type-id="roadmap"
          style="height: 76vh"
        >
        </gmap-map>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeEditPolygon()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="updatePolygon()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ------------ -->
    <!-- 圃場メタデータ編集 -->
    <!-- ------------ -->
    <v-dialog v-model="isNowShowingFieldMetaDataEditor" max-width="550px" persistent>
      <v-card class="pt-4">
        <v-card-text>
          <v-text-field
            v-model="editingFieldName"
            :label="$tt('DocFormFieldDetailFieldNameHint')"
            :placeholder="$tt('DocFormFieldDetailFieldNameHint')"
          />
          <v-textarea
            v-model="editingFieldCaption"
            :label="$tt('DocFormFieldDetailDescriptionHint')"
            :placeholder="$tt('DocFormFieldDetailDescriptionHint')"
            rows="2"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeFieldMetaDataEditor()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="updateFieldMetaData()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ----------------- -->
    <!-- 高低差マップ編集 -->
    <!-- ----------------- -->
    <!-- 圃場属性 (有効期間・作目) 編集 -->
    <v-dialog v-model="attrsDialog" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.fieldAttrsTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="attrsEdit.valid_from"
            type="date"
            dense
            clearable
            :label="$tt('feed.fieldValidFrom')"
          />
          <v-text-field v-model="attrsEdit.valid_to" type="date" dense clearable :label="$tt('feed.fieldValidTo')" />
          <v-text-field v-model="attrsEdit.crop" dense :label="$tt('feed.mapSetCrop')" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="attrsSaving" @click="attrsDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="attrsSaving" @click="saveFieldAttrs()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isNowShowingElevationMapEditor" max-width="550px" persistent>
      <v-card class="pt-4">
        <v-card-text>
          <!-- メッシュサイズ / GNSS Status Filter-->
          <v-row>
            <v-col>
              <v-select
                v-model="selectedElevationMeshSize"
                :items="elevationMeshSizeList"
                :label="$tt('fieldElevation.meshSize')"
                outlined
                hide-details
                dense
              />
            </v-col>
            <v-col>
              <v-select
                v-model="selectedGnssStatusFilter"
                :items="gnssStatusFilterList"
                :label="$tt('fieldElevation.gnssStatus')"
                outlined
                hide-details
                dense
              />
            </v-col>
          </v-row>
          <v-row>
            <!-- 参照データ量 -->
            <v-col>
              <v-slider
                v-model="selectedTaskFetchLimit"
                :min="1"
                :max="30"
                :label="$tt('fieldElevation.taskFetchLimit')"
                :hint="$tt('fieldElevation.taskFetchLimitHint')"
                persistent-hint
              >
                <template v-slot:append>
                  <v-text-field
                    v-model="selectedTaskFetchLimit"
                    class="mt-0 pt-0"
                    hide-details
                    single-line
                    type="number"
                    style="width: 40px"
                    readonly
                  />
                </template>
              </v-slider>
            </v-col>
          </v-row>

          <!-- （オプション）標高レンジ設定 -->
          <v-btn
            v-if="showElevationOption"
            text
            class="mx-0 px-0 pt-3 mt-3"
            @click="toggleElevationDetailOption()"
            small
            style="text-transform: none"
          >
            {{ $tt('fieldElevation.optionSetting') }}
            <v-icon>mdi-chevron-double-right</v-icon>
          </v-btn>
          <v-sheet v-if="useElevationOption" class="mt-2">
            <v-card>
              <v-card-text>
                <v-row dense>
                  <v-col cols="12">
                    <v-checkbox
                      v-model="useZRange"
                      :label="$tt('fieldElevation.elevationRange')"
                      class="my-0 py-0"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      dense
                      :disabled="!useZRange"
                      v-model.number="zRangeMin"
                      type="number"
                      step="0.01"
                      :label="$tt('fieldElevation.minValue')"
                      :hint="$tt('fieldElevation.minIgnoreCaption')"
                      suffix="(m)"
                      @change="onChangeGivenZMin"
                      :error="zRangeErrors.min.error"
                      :error-messages="zRangeErrors.min.message"
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      dense
                      :disabled="!useZRange"
                      v-model.number="zRangeMax"
                      type="number"
                      step="0.01"
                      :label="$tt('fieldElevation.maxValue')"
                      :hint="$tt('fieldElevation.maxIgnoreCaption')"
                      suffix="(m)"
                      @change="onChangeGivenZMax"
                      :error="zRangeErrors.max.error"
                      :error-messages="zRangeErrors.max.message"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- （オプション）日付範囲設定 -->
            <v-card class="mt-2">
              <v-card-text>
                <v-row dense>
                  <v-col cols="12">
                    <v-checkbox
                      :label="$tt('fieldElevation.dateRangeInput')"
                      v-model="useDateRange"
                      class="my-0 py-0 pb-2"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-menu
                      :disabled="!useDateRange"
                      v-model="showingTaskStartDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :disabled="!useDateRange"
                          v-model="oldestTaskDate"
                          :label="$tt('fieldElevation.oldestTaskDateInput')"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :error="dateRangeErrors.start.error"
                          :error-messages="dateRangeErrors.start.message"
                        />
                      </template>
                      <v-date-picker
                        :disabled="!useDateRange"
                        no-title
                        scrollable
                        :day-format="date => new Date(date).getDate()"
                        v-model="oldestTaskDate"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="6">
                    <v-menu
                      :disabled="!useDateRange"
                      v-model="showingTaskEndDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :disabled="!useDateRange"
                          v-model="newestTaskDate"
                          :label="$tt('fieldElevation.newestTaskDateInput')"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :error="dateRangeErrors.end.error"
                          :error-messages="dateRangeErrors.end.message"
                        />
                      </template>
                      <v-date-picker
                        :disabled="!useDateRange"
                        no-title
                        scrollable
                        :day-format="date => new Date(date).getDate()"
                        v-model="newestTaskDate"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-sheet>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn class="secondary" @click="closeElevationMapEditor()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn class="primary" @click="createFieldElevationMap()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <now-loading-overlay-component :nowLoading="nowLoading" />
  </v-container>
</template>

<script>
import FieldElevationSummaryComponent from '@/components/elevation/FieldElevationSummaryComponent'
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import MapMixin from '@/components/mixins/map-mixin'
import MapGaugeMixin from '@/components/mixins/map-gauge-mixin'
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import UserIconComponent from '@/components/user/UserIconComponent'
import Account from '@/lib/account'
import * as Constants from '@/lib/constants'
import i18n from '@/lib/i18n'
import WeatherForecast from '@/lib/open-weather-map/weather-forecast'
import WeatherInfo from '@/lib/open-weather-map/weather-info'
import Planting from '@/lib/planting'
import * as SystemOfUnits from '@/lib/system-of-units'
import Toast from '@/lib/toast'
import feedApiClient from '@/lib/feed-api-client'
import UserInfo from '@/lib/user-info'
import store from '@/store'
import convertUnits from 'convert-units'
import ECharts from 'vue-echarts'
import UTCDateTime from '@/lib/utc-datetime'
import moment from 'moment'

export default {
  name: 'FieldDetail',

  components: {
    PlantingIndicatorComponent,
    UserIconComponent,
    NowLoadingOverlayComponent,
    FieldElevationSummaryComponent,
    ECharts,
  },

  mixins: [MapMixin, GoogleMapMixin, MapGaugeMixin],

  data() {
    return {
      weather: { current: new WeatherInfo(), forecasts: new WeatherForecast() },

      isNowShowingFieldMetaDataEditor: false,
      editingFieldName: undefined,
      editingFieldCaption: undefined,

      showingPastPlantings: false,
      togglePastPlantingsButtonCaption: i18n.tt('customPlantingShowPast'),

      aidGreen: Constants.CONNECT_GREEN,

      isShowEditPolygon: false,
      coordinates: [],
      editPolygon: null,
      updateField: null,
      nowLoading: false,


      echartsTheme: null,

      isNowShowingElevationMapEditor: false,
      showElevationMap: false,
      elevationColorRangeOptions: null,

      elevationMeshSizeList: [{ text: '3m', value: 3 }, { text: '5m', value: 5 }, { text: '10m', value: 10 }],
      selectedElevationMeshSize: Constants.FieldElevationMap.DEFAULT_MESH_SIZE,
      selectedTaskFetchLimit: Constants.FieldElevationMap.DEFAULT_TASK_FETCH_LIMIT,

      gnssStatusFilterList: Constants.FieldElevationMap.GNSS_STATUS_FILTERS,
      selectedGnssStatusFilter: Constants.FieldElevationMap.DEFAULT_GNSS_STATUS_FILTER_VALUE,

      showElevationOption: true,
      useElevationOption: false,
      useZRange: false,
      zRangeMin: null,
      zRangeMax: null,

      useDateRange: false,
      oldestTaskDate: null,
      newestTaskDate: null,
      showingTaskStartDateMenu: false,
      showingTaskEndDateMenu: false,

      zRangeErrors: null,

      // 圃場属性 (有効期間・作目)
      fieldAttrs: { valid_from: '', valid_to: '', crop: '' },
      attrsEdit: { valid_from: '', valid_to: '', crop: '' },
      attrsDialog: false,
      attrsSaving: false,
    }
  },

  mounted: function() {
    // google map
    this.$store.dispatch('setFieldGeoJsons', { type: 'FeatureCollection', features: [] })
    this.drawAllFieldsOnGoogleMap()

    // 圃場属性 (有効期間・作目)
    this.loadFieldAttrs()

    // 高低差マップ
    this.initZRangeErrors()
    this.initDateRangeErrors()
    const params = { field: this.field, onSuccess: this.initElevationMap }
    this.$store.dispatch('getFieldElevationMap', params)
  },

  destroyed: function() {
    this.$store.dispatch('clearCurrentFieldElevationMap')
  },

  computed: {
    hasStandardFunctionality() {
      return UserInfo.hasStandardFunctionality()
    },
    isDarkMode() {
      if (this.$vuetify.theme.dark) {
        return true
      }
      return false
    },

    field() {
      return this.$store.getters.currentField
    },
    isMyField() {
      if (this.field) {
        return this.$store.getters.loginUsername === this.field.username
      }
      return false
    },

    growCrops() {
      // FIXME GROW
      return this.$store.getters.growCrops
    },

    pastPlantings() {
      return Planting.listPastForField(this.field)
    },

    currentPlantings() {
      return Planting.listCurrentForField(this.field)
    },

    // google map
    features() {
      return this.$store.getters.fieldGeoJsons.features
    },


    countTasksInCurrentField() {
      return this.$store.getters.countTasksInCurrentField
    },

    currentFieldElevationMap() {
      return this.$store.getters.currentFieldElevationMap
    },

    showElevationColorGauge() {
      let bool = this.isMyField && this.countTasksInCurrentField > 0
      if (bool) {
        const m = this.currentFieldElevationMap
        if (m) {
          return m.status === 'Completed'
        }
      }
      return false
    },
  },

  watch: {
    currentFieldElevationMap(newValue) {
      if (newValue) {
        this.showElevationMap = true
      }
    },
    showElevationMap(newValue) {
      const updateFieldPolygonStyle = this.updateFieldPolygonStyle
      this.$refs.googleMapRef.$mapPromise.then(map => {
        updateFieldPolygonStyle(map)
      })
    },
    features(newValue) {
      if (newValue.length > 0) {
        this.initMap()
      }
    },
    isDarkMode(newVal, oldVal) {
      if (newVal) {
        this.echartsTheme = 'dark'
      } else {
        this.echartsTheme = null
      }
    },
  },

  methods: {
    async loadFieldAttrs() {
      try {
        const res = await feedApiClient.fieldAttrs()
        const a = (res.data.attrs || {})[String(this.field && this.field.pk)] || {}
        this.fieldAttrs = { valid_from: a.valid_from || '', valid_to: a.valid_to || '', crop: a.crop || '' }
      } catch (e) {
        console.error(e.response || e)
      }
    },
    openFieldAttrsEditor() {
      this.attrsEdit = { ...this.fieldAttrs }
      this.attrsDialog = true
    },
    async saveFieldAttrs() {
      this.attrsSaving = true
      try {
        await feedApiClient.putFieldAttrs(String(this.field.pk), {
          valid_from: this.attrsEdit.valid_from || null,
          valid_to: this.attrsEdit.valid_to || null,
          crop: this.attrsEdit.crop || null,
        })
        this.fieldAttrs = { ...this.attrsEdit }
        this.attrsDialog = false
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.saveFailed')
      } finally {
        this.attrsSaving = false
      }
    },
    initMap() {
      const currentFieldPk = this.field.pk
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.data.forEach(feature => {
          map.data.remove(feature)
        })
        map.setTilt(0)
        map.data.addGeoJson(this.$store.getters.fieldGeoJsons)

        this.updateFieldPolygonStyle(map)

        let bounds = new google.maps.LatLngBounds()
        map.data.forEach(feature => {
          const pk = feature.getProperty(Constants.GeoFeaturePropertiesType.PK)
          if (pk === currentFieldPk) {
            feature.getGeometry().forEachLatLng(latlng => {
              bounds.extend(latlng)
            })
          }
        })
        map.fitBounds(bounds)
      })
    },

    updateFieldPolygonStyle(map) {
      let opacity = Constants.FIELD_FILL_OPACITY
      let renderElevationMap = false

      if (this.showElevationMap) {
        renderElevationMap = true
      }

      map.data.setStyle(feature => {
        const aidType = feature.getProperty('aid-type')
        // 高低差マップ対応
        if (aidType === 'field-elevation-mesh' || aidType === 'field-elevation-field') {
          let zIndex = 200
          if (aidType === 'field-elevation-field') {
            zIndex = 201
          }
          let elevationOpacity = 0
          let strokeOpacity = 0
          if (renderElevationMap) {
            elevationOpacity = feature.getProperty('fill-opacity')
            strokeOpacity = 1
          }
          return {
            fillColor: feature.getProperty('fill'),
            fillOpacity: elevationOpacity,
            strokeColor: feature.getProperty('stroke'),
            strokeWeight: feature.getProperty('stroke-width'),
            strokeOpacity: strokeOpacity,
            zIndex: zIndex,
          }
        }
        const pk = feature.getProperty(Constants.GeoFeaturePropertiesType.PK)
        if (pk === this.field.pk) {
          return {
            fillColor: Constants.FIELD_FILL_COLOR,
            fillOpacity: opacity,
            strokeColor: Constants.FIELD_CURRENT_LINE_COLOR,
            strokeWeight: Constants.FIELD_CURRENT_LINE_WIDTH,
            zIndex: 1,
          }
        } else {
          return {
            fillColor: Constants.FIELD_FILL_COLOR,
            fillOpacity: opacity,
            strokeColor: Constants.FIELD_FILL_COLOR,
            strokeWeight: Constants.FIELD_LINE_WIDTH,
            zIndex: 0,
          }
        }
      })
    },

    showEditPolygon() {
      this.isShowEditPolygon = true
      this.setEditMap()
    },
    setEditMap() {
      const geoJson = JSON.parse(this.field.geoJson)
      let points = []
      geoJson.features.forEach(feature => {
        const coor = feature.geometry.coordinates[0]
        coor.forEach(latlng => {
          const point = {
            lng: latlng[0],
            lat: latlng[1],
          }
          points.push(point)
        })
      })
      if (this.editPolygon) {
        this.editPolygon.setMap(null)
      }
      this.editPolygon = new google.maps.Polygon({
        paths: points,
        strokeColor: Constants.FIELD_CURRENT_LINE_COLOR,
        fillColor: Constants.FIELD_FILL_COLOR,
        strokeWeight: Constants.FIELD_CURRENT_LINE_WIDTH,
        fillOpacity: Constants.FIELD_FILL_OPACITY,
      })
      this.editPolygon.setEditable(true)
      this.$refs.editPolygonMap.$mapPromise.then(map => {
        map.setTilt(0)
        this.editPolygon.setMap(map)
        const vm = this
        google.maps.event.addListener(this.editPolygon, 'dragend', function() {
          vm.setCoordinates()
        })
        google.maps.event.addListener(this.editPolygon, 'mouseup', function() {
          vm.setCoordinates()
        })
      })
    },
    setCoordinates() {
      const coordinates = this.editPolygon.getPath().getArray()
      let coordinate = []
      this.coordinates = []
      coordinates.forEach(point => {
        let points = []
        points.push(point.toJSON().lng)
        points.push(point.toJSON().lat)
        coordinate.push(points)
      })
      this.coordinates.push(coordinate)
    },
    updatePolygon() {
      if (this.coordinates.length < 1) {
        this.closeEditPolygon()
        return
      }
      this.openNowLoading()
      const features = [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: this.coordinates,
          },
        },
      ]
      const request = {
        field: this.field,
        features: features,
        onSuccess: this.updatePolygonSuccess,
        onFailure: this.updatePolygonFailure,
      }
      this.$store.dispatch('updateFieldPolygon', request)
    },
    updatePolygonSuccess(field) {
      this.updateField = field
      const params = { onSuccess: this.reloadField }
      this.$store.dispatch('getAllFields', params)
    },
    updatePolygonFailure() {
      Toast.i18nError('customUpdatePolygonFailure')
      this.closeNowLoading()
    },
    reloadField() {
      this.$store.dispatch('setFieldGeoJsons', { type: 'FeatureCollection', features: [] })
      this.drawAllFieldsOnGoogleMap()
      this.$store.dispatch('setFieldDetail2', this.updateField)
      this.closeNowLoading()
      this.closeEditPolygon()
    },
    openNowLoading() {
      this.nowLoading = true
    },
    closeNowLoading() {
      this.nowLoading = false
    },
    closeEditPolygon() {
      this.isShowEditPolygon = false
    },
    openFieldMetaDataEditor() {
      if (this.isMyField) {
        this.isNowShowingFieldMetaDataEditor = true
        this.editingFieldName = this.field.name
        this.editingFieldCaption = this.field.caption
      }
    },

    closeFieldMetaDataEditor() {
      this.isNowShowingFieldMetaDataEditor = false
      this.editingFieldName = undefined
      this.editingFieldCaption = undefined
    },

    updateFieldMetaData() {
      const updateRequest = {
        field: this.field,
        name: this.editingFieldName,
        caption: this.editingFieldCaption,
        onSuccess: this.updateFieldMetaDataSuccess,
        onFailure: this.updateFieldMetaDataFailure,
      }
      this.openNowLoading()
      this.$store.dispatch('updateFieldMetaData', updateRequest)
      this.closeFieldMetaDataEditor()
    },
    updateFieldMetaDataSuccess() {
      this.closeNowLoading()
    },
    updateFieldMetaDataFailure() {
      this.closeNowLoading()
    },
    onGrowCropSelectionChanged() {
      // masterCropIdがその他の場合
      if (this.selectedMasterCropId === -1) {
        this.openCropOptions()
      } else {
        this.closeCropOptions()
      }
    },

    togglePastPlantings() {
      this.showingPastPlantings = !this.showingPastPlantings
      if (this.showingPastPlantings) {
        this.togglePastPlantingsButtonCaption = i18n.tt('customPlantingHidePast')
      } else {
        this.togglePastPlantingsButtonCaption = i18n.tt('customPlantingShowPast')
      }
    },

    getAccount(field) {
      return Account.getByField(field)
    },

    handleGetCurrentWeatherSuccess(weather) {
      this.weather.current = weather
    },
    handleGetCurrentWeatherFailure(error) {
      console.error(error)
    },
    handleGetForecastWeatherSuccess(forecast) {
      this.weather.forecasts = forecast.getListSkipBy(1, 4)
    },
    handleGetForecastWeatherFailure(error) {
      console.error(error)
    },
    getDatetimeStringAsDateTimeFormat(weatherInfo) {
      return WeatherInfo.getDatetimeStringAsDateTimeFormat(weatherInfo)
    },
    getTemperatureStringRounded(weatherInfo) {
      return WeatherInfo.getTemperatureStringRounded(weatherInfo)
    },
    getRain3hStringRounded(weatherInfo) {
      return WeatherInfo.getRain3hStringRounded(weatherInfo)
    },

    initElevationMap() {
      // GoogleMapへ描画
      const fieldElevationMap = this.$store.getters.currentFieldElevationMap
      this.$refs.googleMapRef.$mapPromise.then(map => {
        // 高低差マップ
        if (fieldElevationMap) {
          const geoJson = JSON.parse(fieldElevationMap.geoJson)
          if (geoJson != null) {
            map.data.addGeoJson(geoJson)
          }
        }
        this.updateFieldPolygonStyle(map)
        this.setUpElevationColorRangeOptions()
      })
      // 高低差マップ生成オプションの初期化
      if (fieldElevationMap) {
        if (this.zRangeMin != null && this.zRangeMax != null) {
          this.useZRange = true
        } else {
          this.useZRange = false
        }

        let date = fieldElevationMap.oldestTaskDate
        if (date) {
          let utc = UTCDateTime.toUtcMoment(date)
          this.oldestTaskDate = utc.tz(Constants.DEFAULT_TIMEZONE).format('yyyy-MM-DD')
        }
        date = fieldElevationMap.newestTaskDate
        if (date) {
          let utc = UTCDateTime.toUtcMoment(date)
          this.newestTaskDate = utc.tz(Constants.DEFAULT_TIMEZONE).format('yyyy-MM-DD')
        }
        if (this.oldestTaskDate && this.newestTaskDate) {
          this.useDateRange = true
        }
      }
    },

    setUpElevationColorRangeOptions() {
      // ゲージ表示
      let opts = {
        grid: { left: '2%', right: '2%', top: 10, bottom: 0, containLabel: true },
        xAxis: { type: 'category', data: [''], show: false },
        yAxis: { type: 'value', axisLabel: { formatter: '{value}' } },
      }

      const colorRanges = this.$store.getters.currentFieldElevationMap.colorRanges
      if (colorRanges) {
        const stageCount = colorRanges.ranges.length
        let minValue = colorRanges.ranges[0].min
        let maxValue = colorRanges.ranges[stageCount - 1].max
        let targetUnit = ''
        if (SystemOfUnits.isMetric()) {
          targetUnit = 'm'
        } else {
          targetUnit = 'ft'
          minValue = convertUnits(minValue)
            .from('m')
            .to('ft')
          maxValue = convertUnits(maxValue)
            .from('m')
            .to('ft')
        }
        opts.yAxis.axisLabel.formatter = '{value}' + targetUnit

        opts = this.createMapGaugeOption(opts, minValue, maxValue, stageCount)

        this.elevationColorRangeOptions = opts
      }
    },
    openElevationMapEditor() {
      this.isNowShowingElevationMapEditor = true
      if (this.currentFieldElevationMap) {
        this.selectedElevationMeshSize = this.currentFieldElevationMap.meshSize
        this.selectedTaskFetchLimit = this.currentFieldElevationMap.taskFetchLimit
        this.selectedGnssStatusFilter = this.currentFieldElevationMap.gnssStatusFilter
        this.zRangeMin = this.currentFieldElevationMap.zRangeMin
        this.zRangeMax = this.currentFieldElevationMap.zRangeMax
        if (this.useZRange || this.useDateRange) {
          this.useElevationOption = true
        } else {
          this.useElevationOption = false
        }
      } else {
        this.selectedElevationMeshSize = Constants.FieldElevationMap.DEFAULT_MESH_SIZE
        this.selectedTaskFetchLimit = Constants.FieldElevationMap.DEFAULT_TASK_FETCH_LIMIT
        this.selectedGnssStatusFilter = Constants.FieldElevationMap.DEFAULT_GNSS_STATUS_FILTER_VALUE
        this.zRangeMin = null
        this.zRangeMax = null
        this.useZRange = false
        this.useElevationOption = false
      }
    },
    closeElevationMapEditor() {
      this.isNowShowingElevationMapEditor = false
      this.selectedElevationMeshSize = Constants.FieldElevationMap.DEFAULT_MESH_SIZE
      this.selectedTaskFetchLimit = Constants.FieldElevationMap.DEFAULT_TASK_FETCH_LIMIT
    },

    createFieldElevationMap() {
      // 標高レンジのエラーチェック
      this.initZRangeErrors()
      if (this.useZRange) {
        if (this.zRangeMin === undefined || this.zRangeMin === null) {
          this.zRangeErrors.min.error = true
          this.zRangeErrors.min.message = i18n.tt('fieldElevation.minValueNeedError')
          return
        }
        if (this.zRangeMax === undefined || this.zRangeMax === null) {
          this.zRangeErrors.max.error = true
          this.zRangeErrors.max.message = i18n.tt('fieldElevation.maxValueNeedError')
          return
        }
        if (this.zRangeMin > this.zRangeMax) {
          this.zRangeErrors.min.error = true
          this.zRangeErrors.min.message = i18n.tt('fieldElevation.minValueRangeError')
          this.zRangeErrors.max.error = true
          this.zRangeErrors.max.message = i18n.tt('fieldElevation.maxValueRangeError')
          return
        }
      }

      // 日付範囲のエラーチェック
      this.initDateRangeErrors()
      if (this.useDateRange) {
        if (!this.oldestTaskDate) {
          this.dateRangeErrors.start.error = true
          this.dateRangeErrors.start.message = i18n.tt('fieldElevation.startDateNeedError')
          return
        }
        if (!this.newestTaskDate) {
          this.dateRangeErrors.end.error = true
          this.dateRangeErrors.end.message = i18n.tt('fieldElevation.endDateNeedError')
          return
        }
        const oldest = UTCDateTime.toUtcMoment(this.oldestTaskDate)
        const newest = UTCDateTime.toUtcMoment(this.newestTaskDate)
        if (oldest > newest) {
          this.dateRangeErrors.start.error = true
          this.dateRangeErrors.start.message = i18n.tt('fieldElevation.startDateRangeError')
          this.dateRangeErrors.end.error = true
          this.dateRangeErrors.end.message = i18n.tt('fieldElevation.endDateRangeError')
          return
        }
      }

      this.initMap()
      const onSuccess = () => {
        this.showElevationMap = true
        this.initElevationMap()
      }
      const onFailure = error => {
        const response = error.response
        if (response) {
          const status = response.status
          if (status === 406) {
            // VertexEmptyException
            Toast.error(i18n.tt('fieldElevation.invalidOptionSettingError'))
          }
        }

        this.showElevationMap = false
        this.$store.dispatch('clearCurrentFieldElevationMap')
        Toast.error(i18n.tt('fieldElevation.creationFailedMessage'))
      }

      let zRangeOpts = null
      if (this.useZRange) {
        zRangeOpts = { min: this.zRangeMin, max: this.zRangeMax }
      }

      let dateRangeOpts = null
      if (this.useDateRange) {
        dateRangeOpts = { oldest: this.oldestTaskDate, newest: this.newestTaskDate }
      }

      const params = {
        field: this.field,
        meshSize: this.selectedElevationMeshSize,
        maxPointCount: 100000,
        taskFetchLimit: this.selectedTaskFetchLimit,
        gnssStatusFilter: this.selectedGnssStatusFilter,
        zRange: zRangeOpts,
        dateRange: dateRangeOpts,
        onSuccess: onSuccess,
        onFailure: onFailure,
      }
      this.$store.dispatch('createFieldElevationMap', params)
      this.closeElevationMapEditor()
    },

    toggleElevationDetailOption() {
      this.useElevationOption = !this.useElevationOption
    },

    initZRangeErrors() {
      this.zRangeErrors = { min: { error: false, message: null }, max: { error: false, message: null } }
    },

    initDateRangeErrors() {
      this.dateRangeErrors = {
        start: { error: false, message: null },
        end: { error: false, message: null },
      }
    },

    onChangeGivenZMin(value) {
      if (value) {
        this.zRangeMin = Math.round(value * 100) / 100
      }
    },
    onChangeGivenZMax(value) {
      if (value) {
        this.zRangeMax = Math.round(value * 100) / 100
      }
    },
  },
}
</script>
<style scoped>
.elevationColorGaugeContainer {
  position: absolute;
  top: 65px;
  left: 10px;
  width: 120px;
}

.elevationColorGauge {
  position: relative;
  width: 120px;
  height: 180px;
}
</style>
